import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Calendar, ExternalLink } from 'lucide-react';
import api from '../../../api/client';
import AptiviewLoader from '../../common/AptiviewLoader/AptiviewLoader';
import {
  STATUS_CONFIG,
  adminInitials,
  formatCardDate,
  formatRate,
  ratePercent,
  toDateParam,
} from './recruiterActivityUtils';

const CARD_LIMIT = 20;

export default function RecruiterActivityDetail({
  recruiter,
  companyId,
  period,
  periodStart,
  periodEnd,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const listRef = useRef(null);
  const [cards, setCards] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestIdRef = useRef(0);
  const fetchingRef = useRef(false);

  const fetchCards = useCallback(async (cursor) => {
    if (!companyId || !recruiter?.id) return;
    if (fetchingRef.current) return;
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    fetchingRef.current = true;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        company_id: String(companyId),
        period,
        limit: String(CARD_LIMIT),
      });
      if (cursor) params.set('cursor', cursor);
      const response = await api.get(
        `v1/recruiter-activity/recruiters/${recruiter.id}/interviews/?${params.toString()}`
      );
      if (requestId !== requestIdRef.current) return;
      const results = Array.isArray(response.data?.results) ? response.data.results : [];
      setCards((prev) => (cursor ? [...prev, ...results] : results));
      setNextCursor(response.data?.next_cursor || null);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(
        err.response?.data?.error
        || t('recruiterActivity.cardsError', 'Could not load interviews for this person.')
      );
    } finally {
      if (requestId === requestIdRef.current) {
        fetchingRef.current = false;
        setLoading(false);
      }
    }
  }, [companyId, period, recruiter?.id, t]);

  useEffect(() => {
    fetchingRef.current = false;
    requestIdRef.current += 1;
    if (listRef.current) listRef.current.scrollTop = 0;
    setCards([]);
    setNextCursor(null);
    setError('');
    if (!recruiter?.id || recruiter.scheduled === 0) return;
    fetchCards(null);
  }, [companyId, period, recruiter?.id, recruiter?.scheduled, fetchCards]);

  const handleListScroll = (event) => {
    if (loading || fetchingRef.current || !nextCursor) return;
    const node = event.currentTarget;
    if (node.scrollTop + node.clientHeight >= node.scrollHeight - 56) {
      fetchCards(nextCursor);
    }
  };

  if (!recruiter) return null;

  const isIdle = recruiter.scheduled === 0;
  const pct = ratePercent(recruiter.completion_rate);
  const roleLabel = recruiter.role === 'user_manager'
    ? t('recruiterActivity.roleUserManager', 'User Manager')
    : t('recruiterActivity.roleCompanyAdmin', 'Company Admin');

  const openOverview = () => {
    const params = new URLSearchParams();
    params.set('scheduled_by', String(recruiter.id));
    if (companyId) params.set('company_id', String(companyId));
    const from = toDateParam(periodStart);
    const to = toDateParam(periodEnd);
    if (from) params.set('filter_date_from', from);
    if (to) params.set('filter_date_to', to);
    navigate(`/interview-overview?${params.toString()}`);
  };

  return (
    <section
      className={`flex h-full min-h-0 w-full flex-col overflow-hidden rounded-xl border ${
        isIdle ? 'border-amber-200 bg-amber-50/50' : 'border-gray-200 bg-white'
      }`}
    >
      <header className="flex-shrink-0 border-b border-gray-100 p-3">
        <div className="flex items-start gap-2.5">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-indigo-600 text-[11px] font-semibold tracking-wide text-white">
            {adminInitials(recruiter.name)}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <h2 className="truncate text-sm font-semibold text-gray-900">{recruiter.name || '—'}</h2>
              {isIdle && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                  {t('recruiterActivity.idleBadge', 'Idle')}
                </span>
              )}
            </div>
            <p className="truncate text-[11px] text-gray-500">{roleLabel}</p>
          </div>
        </div>

        <div className="mt-2.5 flex items-center gap-2">
          <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-gray-100">
            <div className="h-full rounded-full bg-sky-500" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-xs font-semibold text-sky-700">{formatRate(recruiter.completion_rate)}</span>
        </div>

        <dl className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
          <Metric label={t('recruiterActivity.scheduled', 'Scheduled')} value={recruiter.scheduled} />
          <Metric label={t('recruiterActivity.completed', 'Completed')} value={recruiter.completed} className="text-emerald-700" />
          <Metric label={t('recruiterActivity.pending', 'Pending')} value={recruiter.pending || 0} className="text-amber-700" />
          <Metric label={t('recruiterActivity.expired', 'Expired')} value={recruiter.expired} className="text-rose-700" />
        </dl>

        {recruiter.scheduled > 0 && (
          <button
            type="button"
            onClick={openOverview}
            className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-medium text-sky-700 shadow-2xs hover:bg-sky-50"
          >
            {t('recruiterActivity.viewOverview', 'View in Overview')}
            <ExternalLink size={11} />
          </button>
        )}
      </header>

      <div
        ref={listRef}
        onScroll={handleListScroll}
        className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2.5"
      >
        {isIdle && !loading && cards.length === 0 && !error && (
          <p className="px-2 py-8 text-center text-sm text-amber-800">
            {t('recruiterActivity.idleColumn', 'No interviews scheduled in this period')}
          </p>
        )}
        {!isIdle && !loading && cards.length === 0 && !error && (
          <p className="px-2 py-8 text-center text-sm text-gray-500">
            {t('recruiterActivity.emptyColumn', 'No interviews scheduled in this period')}
          </p>
        )}
        {error && <p className="px-2 py-4 text-center text-sm text-rose-600">{error}</p>}
        {cards.map((card) => {
          const status = STATUS_CONFIG[card.status] || STATUS_CONFIG.scheduled;
          return (
            <button
              key={card.id}
              type="button"
              onClick={openOverview}
              className="w-full rounded-lg border border-gray-200 bg-white p-2.5 text-left shadow-sm transition hover:border-sky-300 hover:shadow"
            >
              <div className="flex items-start justify-between gap-2">
                <p className="truncate text-sm font-semibold text-gray-900">{card.candidate_name || '—'}</p>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${status.className}`}>
                  {t(status.key, status.fallback)}
                </span>
              </div>
              <p className="mt-1 flex items-center gap-1.5 truncate text-xs text-gray-600">
                <Briefcase size={12} className="flex-shrink-0 text-gray-400" />
                {card.job_title || '—'}
              </p>
              <p className="mt-1 flex items-center gap-1.5 text-xs text-gray-500">
                <Calendar size={12} className="flex-shrink-0 text-gray-400" />
                {formatCardDate(card.created_at)}
              </p>
            </button>
          );
        })}
        {loading && (
          <div className="flex justify-center py-4">
            <AptiviewLoader size="sm" message={t('common.loading', 'Loading…')} />
          </div>
        )}
        {nextCursor && !loading && (
          <button
            type="button"
            onClick={() => fetchCards(nextCursor)}
            className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-sky-700 hover:bg-sky-50"
          >
            {t('recruiterActivity.loadMore', 'Load more')}
          </button>
        )}
      </div>
    </section>
  );
}

function Metric({ label, value, className = 'text-gray-900' }) {
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-gray-500">{label}</dt>
      <dd className={`font-semibold ${className}`}>{value}</dd>
    </div>
  );
}
